// Articles index stylesheet, verbatim from the hand-built articles.html design.
// Injected scoped under `.trex-articles` via scopeCSS, so the `:root`, `*` and
// `body` rules cannot leak into the rest of the app.
// Two adaptations only: the sticky filter bar sits under the 60px site navbar,
// and the feed keeps the site footer clear.
export const STYLES = `
:root{
  --bone:#FAF8F4; --aub:#15102A; --purple:#4C3778; --champ:#A88B4A; --champ-light:#E8D9B8;
  --ink:#2E2842; --slate:#57516B; --faint:#8A859A; --mist:#B7B3C3;
  --border:#DCD9E2; --hair:#EAE8EE; --tint:#F3F1F5; --purple-50:#E9E5F8;
  --ui:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
  --serif:'Source Serif 4',Georgia,serif;
  --mono:'JetBrains Mono',Consolas,monospace;
  --ease:cubic-bezier(.22,.8,.3,1);
  --gutter:16px; --max:1200px;
}
*{box-sizing:border-box}
body{margin:0;background:var(--bone);color:var(--ink);font:400 16px/1.6 var(--ui);
  -webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
a{color:inherit;text-decoration:none}
button{font:inherit;color:inherit}
img{display:block}
.wrap{max-width:var(--max);margin:0 auto;padding:0 var(--gutter)}
:focus-visible{outline:2px solid var(--purple);outline-offset:3px;border-radius:2px}
.sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
@media (min-width:640px){:root{--gutter:28px}}
@media (min-width:1024px){:root{--gutter:40px}}

/* header: dark, hard-edged, the field is a shader */
.hero{position:relative;overflow:hidden;background:var(--aub);color:var(--bone);isolation:isolate;
  border-bottom:1px solid rgba(250,248,244,.07)}
.hero canvas{position:absolute;inset:0;width:100%;height:100%;display:block;z-index:-1}
.hero .veil{position:absolute;inset:0;z-index:0;pointer-events:none;
  background:linear-gradient(90deg,rgba(21,16,42,.18) 0%,rgba(21,16,42,0) 50%)}
.hero .wrap{max-width:1260px;padding-top:120px;padding-bottom:104px;position:relative;z-index:1}
.hero h1{font:600 clamp(46px,6.6vw,84px)/1.02 var(--serif);letter-spacing:-.024em;margin:0;color:var(--bone);max-width:12ch}
.hero p{max-width:560px;margin:26px 0 0;color:rgba(250,248,244,.74);font-size:18px;line-height:1.55}
.hero .in{animation:settle .9s var(--ease) both}
.hero p.in{animation-delay:.1s}
@keyframes settle{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@media (max-width:639px){.hero .wrap{padding-top:96px;padding-bottom:64px}.hero p{font-size:16px;margin-top:18px}}

/* filters: the site's mono buttons, sticky under the navbar */
.filters{position:sticky;top:60px;z-index:20;background:rgba(250,248,244,.86);
  -webkit-backdrop-filter:blur(14px);backdrop-filter:blur(14px);
  border-bottom:1px solid transparent;transition:border-color .25s,box-shadow .25s}
.filters.stuck{border-bottom-color:var(--hair);box-shadow:0 10px 30px -18px rgba(21,16,42,.2)}
.filters .wrap{max-width:1260px}
.filters .scroll{overflow-x:auto;scrollbar-width:none;margin:0 calc(var(--gutter) * -1);padding:0 var(--gutter);-webkit-overflow-scrolling:touch}
.filters .scroll::-webkit-scrollbar{display:none}
.filters .row{position:relative;display:inline-flex;gap:8px;padding:16px 0;min-width:max-content}
.ink{position:absolute;top:16px;left:0;height:36px;width:0;border-radius:4px;border:1px solid var(--champ);background:rgba(168,139,74,.08);
  transition:transform .4s var(--ease),width .4s var(--ease);will-change:transform,width;pointer-events:none;box-sizing:border-box}
.pill{position:relative;z-index:1;flex:none;background:transparent;border:1px solid var(--border);border-radius:4px;
  height:36px;padding:0 14px;font:500 11px/1 var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--slate);cursor:pointer;
  display:inline-flex;align-items:center;gap:9px;white-space:nowrap;transition:color .25s var(--ease),border-color .25s var(--ease)}
.pill:hover{color:var(--aub);border-color:var(--mist)}
.pill[aria-pressed="true"]{color:#806521;border-color:transparent}
.pill .n{font:500 11px/1 var(--mono);font-variant-numeric:tabular-nums;color:var(--faint);letter-spacing:0;transition:color .25s}
.pill[aria-pressed="true"] .n{color:var(--champ)}
.pill:focus-visible{outline-offset:2px}
@media (max-width:639px){.pill{height:44px}.ink{height:44px}.filters .row{padding:12px 0}.ink{top:12px}}

/* the grid: three up, every card the same */
.feed{padding:48px var(--gutter) 112px;max-width:1260px;margin:0 auto;background:var(--bone)}
.grid{display:grid;grid-template-columns:1fr;gap:40px 28px;list-style:none;margin:0;padding:0}
@media (min-width:640px){.grid{grid-template-columns:repeat(2,1fr);gap:44px 28px}}
@media (min-width:1024px){.grid{grid-template-columns:repeat(3,1fr);gap:52px 32px}}

.card{position:relative;display:flex;flex-direction:column;height:100%;cursor:pointer;background:#FFFEFB;border:1px solid var(--border);border-radius:10px;overflow:hidden;
  opacity:0;transform:translateY(16px);
  transition:opacity .55s var(--ease),transform .55s var(--ease),border-color .25s var(--ease),box-shadow .25s var(--ease)}
.card.show{opacity:1;transform:none}
.card:hover{border-color:var(--mist);box-shadow:0 14px 34px -24px rgba(21,16,42,.35)}
.grid.swap .card{transition:opacity .22s ease,transform .22s ease;opacity:0;transform:translateY(6px)}

.shot{position:relative;overflow:hidden;aspect-ratio:7/4;background:var(--aub);isolation:isolate}
.shot img,.shot .gen{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  transition:transform .9s var(--ease),filter .6s var(--ease)}
.shot::after{content:"";position:absolute;inset:0;border-radius:inherit;
  box-shadow:inset 0 0 0 1px rgba(21,16,42,.08);pointer-events:none;z-index:2}
.card:hover .shot img,.card:hover .shot .gen,.card:focus-within .shot img,.card:focus-within .shot .gen{transform:scale(1.04)}
.card:hover .shot img{filter:brightness(1.06)}

/* generated field for pieces that do not have a cover yet */
.gen{display:flex;align-items:center;justify-content:center;flex-direction:column;gap:14px;text-align:center;
  color:var(--bone);padding:24px}
.gen .series{font:400 clamp(22px,2.3vw,28px)/1.1 var(--serif);letter-spacing:-.012em;color:rgba(250,248,244,.94)}
.gen .series i{font-style:italic;color:var(--champ)}
.gen .tag{font:500 10px/1 var(--mono);letter-spacing:.16em;text-transform:uppercase;color:rgba(232,217,184,.72)}

.body{display:flex;flex-direction:column;flex:1;padding:20px 22px 22px}
.meta{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.cat{font:500 11px/1 var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--purple)}
.meta .dot{width:3px;height:3px;border-radius:50%;background:var(--mist)}
.date{font:500 11px/1 var(--mono);letter-spacing:.04em;font-variant-numeric:tabular-nums;color:var(--faint)}
.card h2{font:600 21px/1.28 var(--serif);letter-spacing:-.012em;margin:0 0 10px;color:var(--aub);
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;
  transition:color .25s var(--ease)}
.card:hover h2{color:var(--purple)}
.card p{margin:0;color:var(--slate);font-size:15px;line-height:1.55;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.go{margin-top:auto;padding-top:20px;display:flex;align-items:center;gap:10px;
  font:500 11px/1 var(--mono);letter-spacing:.1em;text-transform:uppercase;color:var(--aub)}
.go .line{display:flex;align-items:center;width:34px;height:12px;flex:none;transition:width .45s var(--ease)}
.go .line i{display:block;width:10px;height:10px;background:var(--champ);flex:none}
.go .line b{display:block;flex:1;height:2px;background:var(--champ);border-radius:1px;margin-left:-4px}
.card:hover .go .line{width:60px}
.go .ext{font:500 10px/1 var(--mono);letter-spacing:.08em;color:var(--faint);margin-left:auto;text-transform:none}
.card .hit{position:absolute;inset:0;z-index:3}

.empty{padding:80px 0;text-align:center;color:var(--faint);font-size:15px}
.empty span{display:block;font:400 24px/1.2 var(--serif);color:var(--aub);margin-bottom:8px}

.load{padding:80px 0;display:flex;justify-content:center}
.load i{width:22px;height:22px;border-radius:50%;border:2px solid var(--border);border-top-color:var(--champ);animation:spin .8s linear infinite;display:block}
@keyframes spin{to{transform:rotate(360deg)}}

@media (prefers-reduced-motion:reduce){
  .hero .in{animation:none}
  .card,.grid.swap .card{opacity:1;transform:none;transition:none}
  .shot img,.shot .gen,.go .line,.ink{transition:none}
}
`;
